import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../TransitioningModal';
import { StyledButton } from '../../common/StyledButton';
import { useModalsContext } from '../../../context/ModalsContextProvider';
import {
  BACKGROUND_COLOR,
  BORDER_COLOR,
  PRIMARY_COLOR,
} from '../../../constants';
import SingleLineInputField from '../../../features/ChatPage/SingleLineInputField';
import { CloseButtonInModal } from '../../common/CloseButtonInModal';
import PreOptionsSection from './PreOptionsSection';
import { desktopMediaQuery } from '../../../styleHelpers';
import { ENABLE_PROMPT_CHECKBOX_NAME } from '../../../hooks/chat/chatHelpers';
import { useChatContext } from '../../../context/ChatContextProvider';

interface PromptModalProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}

const PromptModal = ({ showModal, setShowModal }: PromptModalProps) => {
  const [input, setInput] = useState('');
  const { modalMessage, onConfirmCallback, closePromptModal, preOptions } =
    useModalsContext();

  const { isImagePromptEnabled, setIsImagePromptEnabled } = useChatContext();
  const preOptionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showModal && preOptionsRef.current) {
      preOptionsRef.current.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [showModal, preOptions]);

  const handleConfirm = () => {
    onConfirmCallback?.(input);
    setInput('');
    closePromptModal();
  };

  const handleClose = () => {
    setShowModal(false);
    closePromptModal();
  };

  // Hack to enable prompt checkbox from the TEXT_PROMPT_CHAT_SCENARIO_OPTIONS
  const enablePromptCheckbox = preOptions?.find(
    (option) => option.name === ENABLE_PROMPT_CHECKBOX_NAME,
  );

  return (
    <Modal
      isOpen={showModal}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 100,
        },
        content: {
          position: 'relative',
          backgroundColor: BACKGROUND_COLOR,
          padding: '16px',
          width: '90vw',
          maxWidth: '400px',
          height: 'auto',
          margin: 'auto',
          marginTop: '20vh',
        },
      }}
      ariaHideApp={false}
      shouldFocusAfterRender={false}
      onRequestClose={handleClose}
    >
      <ModalContent>
        <CloseButtonInModal onClick={handleClose} />
        <Message>{modalMessage}</Message>
        <InputContainer
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleConfirm();
            }
          }}
        >
          <SingleLineInputField
            hasBorder
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Write here..."
          />
        </InputContainer>

        {preOptions && preOptions.length > 0 && (
          <PreOptionsSection
            preOptions={preOptions}
            setInput={setInput}
            preOptionsRef={preOptionsRef}
          />
        )}

        {enablePromptCheckbox && (
          <CheckboxContainer
            onClick={() => setIsImagePromptEnabled(!isImagePromptEnabled)}
          >
            <StyledCheckbox
              type="checkbox"
              checked={isImagePromptEnabled}
              onChange={() => setIsImagePromptEnabled(!isImagePromptEnabled)}
            />
            <Label>
              Include image prompt in the context <i>(experimental)</i>
            </Label>
          </CheckboxContainer>
        )}
        <ButtonContainer>
          <StyledButton onClick={handleConfirm}>Ok</StyledButton>
        </ButtonContainer>
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  min-width: 300px;
`;

const Message = styled.p`
  margin: 0;
  font-size: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const InputContainer = styled.div`
  width: 100%;
  margin-left: -16px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  ${desktopMediaQuery} {
    &:hover {
      cursor: pointer;
      color: ${PRIMARY_COLOR};
    }
  }
  width: 100%;
`;

const StyledCheckbox = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  width: 24px;
  height: 20px;
  background: ${BACKGROUND_COLOR};
  border: 2px solid ${BORDER_COLOR};
  border-radius: 4px;
  position: relative;

  &:checked::before {
    content: '';
    position: absolute;
    left: 5px;
    bottom: 3px;
    width: 5px;
    height: 10px;
    border: solid ${PRIMARY_COLOR};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  &:hover {
    cursor: pointer;
    color: ${PRIMARY_COLOR};
  }
`;

const Label = styled.label`
  ${desktopMediaQuery} {
    &:hover {
      cursor: pointer;
      color: ${PRIMARY_COLOR};
    }
  }
  margin-left: 4px;
`;

export default PromptModal;
